import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Loader from '@amzn/meridian/loader';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { getClockWindowNameBasedOnSelectedOption } from '../../../services/shiftPlanService';
import { DEFAULT_SHIFT_HALF_OPTIONS } from '../../../utils/constants';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
  clearStationAssignment,
  selectStationAssignment,
} from '../../../store/slices/stationAssignmentSlice';
import { showError, showNotification } from '../../../store/slices/errorSlice';
import { useNavigationControl } from '../../../hooks/useNavigationControl';
import { ROUTES } from '../../../utils/constants';
import AppModal from '../../../components/AppModal/AppModal';
import AssociateSidebar from './components/AssociateSidebar';
import MetricsBar from './components/MetricsBar';
import ProcessPathSection from './components/ProcessPathSection';
import SearchAssociateModal from './components/SearchAssociateModal';
import { getStationAssignments, publishStationAssignment } from './api';
import { publishShiftPlan } from '../../reviewAndPublish/api';
import {
  AssignmentAssociate,
  AssignModalTarget,
  GetStationAssignmentsResponse,
  StationAssignmentHalfData,
  ProcessPath,
  Workstation,
  StaffingMode,
} from './models';
import {
  assignAssociateToWorkstation,
  filterUnassignedAssociates,
  isAssigned,
  recomputeMetrics,
  removeAssociateFromWorkstation,
} from './utils';
import './index.scss';

/**
 * Full Assignments tab (Tab 2 of the Station Assignment workflow).
 *
 * Responsibilities:
 *   - Fetch the current shift's assignment data on mount (and whenever the
 *     shift selector changes) via `getStationAssignments`.
 *   - Hold the full `GetStationAssignmentsResponse` in local state as the single
 *     source of truth; all derived values (unassigned pool, metrics) are
 *     computed off of that response.
 *   - Delegate all assignment / removal logic to the pure helpers in
 *     `utils.ts`, re-running `recomputeMetrics` after every mutation so
 *     the MetricsBar stays in sync.
 *   - Compose the layout from the previously built presentational pieces:
 *     `AssociateSidebar`, `MetricsBar`, `ProcessPathSection`.
 *
 * Assignment picker:
 *   When the sidebar fires `onAssignClick(associateId)`, this component
 *   opens a small modal listing every direct-path workstation. Selecting
 *   one invokes `assignAssociateToWorkstation` and closes the modal.
 *
 * This component is intentionally a thin renderer plus effect runner;
 * everything interesting lives in `utils.ts` and the child components,
 * both of which are independently tested.
 */
const AssignmentsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { navigate } = useNavigationControl();
  const { selectedDate, selectedShift, selectedHalf, planId } =
    useAppSelector(selectStationAssignment);

  const [response, setResponse] = useState<GetStationAssignmentsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [expandedPaths, setExpandedPaths] = useState<Record<string, boolean>>(
    {},
  );

  // Workstation-picker modal state. `assigningAssociateIds` doubles as the
  // modal's open flag: an empty array means closed. The array preserves the
  // order in which the sidebar reported the checked associates so the
  // assignment proceeds top-down through the list.
  const [assigningAssociateIds, setAssigningAssociateIds] = useState<string[]>(
    [],
  );

  // Confirmation modal shown when the user clicks Verify. Submitting from
  // the modal dispatches a global success notification (via the existing
  // NotificationModal in ErrorModal.tsx) and returns the user to the
  // initial Station Assignment landing page.
  const [verifyModalOpen, setVerifyModalOpen] = useState<boolean>(false);

  // "Assign Associate" modal state. Non-null means the modal is open.
  const [assignModalTarget, setAssignModalTarget] = useState<AssignModalTarget | null>(null);

  // Swap selection state: tracks which associates are checked (max 2).
  const [selectedAssociateIds, setSelectedAssociateIds] = useState<Set<string>>(new Set());
  // When true, the SearchAssociateModal opens in swap mode.
  const [swapModalOpen, setSwapModalOpen] = useState<boolean>(false);

  // Shift and half values come from Redux (set on the generation page and
  // the header controls respectively). They are read-only in this component.
  const shift = selectedShift ?? 'day';
  const half = selectedHalf || DEFAULT_SHIFT_HALF_OPTIONS[0].value;

  /**
   * Fetch assignments for the current selection. Wrapped in useCallback
   * so the effect below only re-runs when inputs actually change.
   */
  const fetchData = useCallback(async () => {
    if (!selectedDate || !planId) {
      setError('Missing required parameters (date or planId).');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const clockWindow = getClockWindowNameBasedOnSelectedOption(shift);
      const result = await getStationAssignments(selectedDate, clockWindow, planId);

      // Treat an empty response (no associates and no lastModifiedBy) as an
      // error surface, mirroring the Shift Rotation page's behaviour.
      if (
        result.firstHalfStationAssignment.associates.length === 0 &&
        result.firstHalfStationAssignment.processPaths.length === 0 &&
        !result.lastModifiedBy
      ) {
        setError('Failed to load assignments data. Please try again.');
        setResponse(null);
      } else {
        setResponse(result);
        // Default every process path to expanded on (re-)load.
        const defaultExpanded: Record<string, boolean> = {};
        result.firstHalfStationAssignment.processPaths.forEach((path) => {
          defaultExpanded[path.fclmFunctionId] = true;
        });
        result.secondHalfStationAssignment.processPaths.forEach((path) => {
          defaultExpanded[path.fclmFunctionId] = true;
        });
        setExpandedPaths(defaultExpanded);
      }
    } catch (err: any) {
      setError(err?.message || 'An unexpected error occurred.');
      setResponse(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, planId, shift]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Derived values ──────────────────────────────────────────────────────

  const activeHalf: StationAssignmentHalfData | null = useMemo(() => {
    if (!response) return null;
    const resolved = response[half as keyof Pick<GetStationAssignmentsResponse, 'firstHalfStationAssignment' | 'secondHalfStationAssignment'>];
    return resolved;
  }, [response, half]);

  const processPaths: ProcessPath[] = activeHalf?.processPaths ?? [];

  /**
   * Compute the unassigned pool from the authoritative path/workstation
   * structure so the sidebar always reflects the latest state without
   * relying on the `assignedWorkstationId` convenience marker.
   */
  const unassignedAssociates: AssignmentAssociate[] = useMemo(() => {
    if (!activeHalf) return [];
    return activeHalf.associates.filter(
      (a) => !isAssigned(a, activeHalf.processPaths),
    );
  }, [activeHalf]);

  const filteredUnassignedAssociates = useMemo(
    () => filterUnassignedAssociates(unassignedAssociates, searchQuery),
    [unassignedAssociates, searchQuery],
  );

  /**
   * Every PROCESS_LINES workstation flattened into `(processPath, workstation)`
   * pairs for the assignment picker modal.
   */
  const allWorkstations: Array<{
    processPath: ProcessPath;
    workstation: Workstation;
  }> = useMemo(() => {
    const out: Array<{ processPath: ProcessPath; workstation: Workstation }> = [];
    processPaths
      .filter((path) => path.staffingMode === StaffingMode.PROCESS_LINES)
      .forEach((path) => {
        path.workstations.forEach((ws) => {
          out.push({ processPath: path, workstation: ws });
        });
      });
    return out;
  }, [processPaths]);

  // ── Handlers ────────────────────────────────────────────────────────────

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const handleAssignClick = (associateIds: string[]) => {
    setAssigningAssociateIds(associateIds);
  };

  const handleCloseAssignModal = () => {
    setAssigningAssociateIds([]);
  };

  const handleWorkstationPick = (workstationId: string) => {
    if (!response || !activeHalf || assigningAssociateIds.length === 0) {
      setAssigningAssociateIds([]);
      return;
    }
    let mutated = activeHalf;
    for (const associateId of assigningAssociateIds) {
      mutated = assignAssociateToWorkstation(
        mutated,
        associateId,
        workstationId,
      );
    }
    const nextMetrics = recomputeMetrics(mutated);
    const updatedHalf = { ...mutated, metrics: nextMetrics };
    setResponse({ ...response, [half]: updatedHalf });
    setAssigningAssociateIds([]);
  };

  /**
   * Shared remove callback for both WorkstationCard (locationId is a
   * workstationId) and IndirectAssociateRow (locationId is a processPathId).
   * The pure helper resolves either kind of id.
   */
  const handleAssociateRemove = (associateId: string, locationId: string) => {
    if (!response || !activeHalf) return;
    const mutated = removeAssociateFromWorkstation(
      activeHalf,
      associateId,
      locationId,
    );
    const nextMetrics = recomputeMetrics(mutated);
    const updatedHalf = { ...mutated, metrics: nextMetrics };
    setResponse({ ...response, [half]: updatedHalf });
  };

  const handleAddAssociate = (target: AssignModalTarget) => {
    setAssignModalTarget(target);
  };

  const handleAssignSubmit = (associateId: string) => {
    if (!response || !activeHalf || !assignModalTarget) return;
    const mutated = assignAssociateToWorkstation(
      activeHalf,
      associateId,
      assignModalTarget.workstationId,
    );
    const nextMetrics = recomputeMetrics(mutated);
    const updatedHalf = { ...mutated, metrics: nextMetrics };
    setResponse({ ...response, [half]: updatedHalf });
    setAssignModalTarget(null);
  };

  const handleCloseSearchModal = () => {
    setAssignModalTarget(null);
  };

  /**
   * Find an associate by ID across both the unassigned pool (top-level
   * `associates` array) and assigned workstations within process paths.
   * The real API only places unassigned associates in the top-level array;
   * assigned ones live exclusively inside processPaths[].workstations[].
   */
  const findAssociateById = useCallback((id: string): AssignmentAssociate | undefined => {
    if (!activeHalf) return undefined;
    // Check unassigned pool first
    const fromPool = activeHalf.associates.find((a) => a.associateId === id);
    if (fromPool) return fromPool;
    // Then check workstation assignments
    for (const path of activeHalf.processPaths) {
      for (const ws of path.workstations) {
        const found = ws.assignedAssociates.find((a) => a.associateId === id);
        if (found) return found;
      }
    }
    /* istanbul ignore next -- defensive: unreachable via UI since selection is constrained to existing associates */
    return undefined;
  }, [activeHalf]);

  const handleSwapSubmit = () => {
    if (!response || !activeHalf || selectedAssociateIds.size !== 2) return;
    const [id1, id2] = Array.from(selectedAssociateIds);

    // Find each associate and their position (workstationId + index within it).
    let associate1: AssignmentAssociate | undefined;
    let associate2: AssignmentAssociate | undefined;
    let ws1Id = '';
    let ws1Idx = -1;
    let ws2Id = '';
    let ws2Idx = -1;

    for (const path of activeHalf.processPaths) {
      for (const ws of path.workstations) {
        const idx1 = ws.assignedAssociates.findIndex((a) => a.associateId === id1);
        if (idx1 !== -1) {
          associate1 = ws.assignedAssociates[idx1];
          ws1Id = ws.workstationId;
          ws1Idx = idx1;
        }
        const idx2 = ws.assignedAssociates.findIndex((a) => a.associateId === id2);
        if (idx2 !== -1) {
          associate2 = ws.assignedAssociates[idx2];
          ws2Id = ws.workstationId;
          ws2Idx = idx2;
        }
      }
    }

    /* istanbul ignore if -- defensive: unreachable via UI since checkboxes only render for workstation-assigned associates */
    if (!associate1 || !associate2 || !ws1Id || !ws2Id) {
      dispatch(showError({
        title: 'Invalid Swap',
        message: 'Both associates must be assigned to a workstation to swap.',
      }));
      setSwapModalOpen(false);
      setSelectedAssociateIds(new Set());
      return;
    }

    // Position-preserving swap: replace each associate at the other's index.
    const swapped1: AssignmentAssociate = { ...associate1, assignedWorkstationId: ws2Id };
    const swapped2: AssignmentAssociate = { ...associate2, assignedWorkstationId: ws1Id };

    const newProcessPaths = activeHalf.processPaths.map((path) => ({
      ...path,
      workstations: path.workstations.map((ws) => {
        if (ws.workstationId === ws1Id && ws.workstationId === ws2Id) {
          // Same workstation: swap at their respective indices.
          const newAssociates = [...ws.assignedAssociates];
          newAssociates[ws1Idx] = swapped2;
          newAssociates[ws2Idx] = swapped1;
          return { ...ws, assignedAssociates: newAssociates };
        }
        if (ws.workstationId === ws1Id) {
          // Replace associate1 at its position with associate2.
          const newAssociates = [...ws.assignedAssociates];
          newAssociates[ws1Idx] = swapped2;
          return { ...ws, assignedAssociates: newAssociates };
        }
        if (ws.workstationId === ws2Id) {
          // Replace associate2 at its position with associate1.
          const newAssociates = [...ws.assignedAssociates];
          newAssociates[ws2Idx] = swapped1;
          return { ...ws, assignedAssociates: newAssociates };
        }
        return ws;
      }),
    }));

    const mutated: StationAssignmentHalfData = {
      ...activeHalf,
      processPaths: newProcessPaths,
    };

    const nextMetrics = recomputeMetrics(mutated);
    const updatedHalf = { ...mutated, metrics: nextMetrics };
    setResponse({ ...response, [half]: updatedHalf });
    setSwapModalOpen(false);
    setSelectedAssociateIds(new Set());
  };

  const handleCloseSwapModal = () => {
    setSwapModalOpen(false);
  };

  const handleToggleSelect = (associateId: string) => {
    setSelectedAssociateIds((prev) => {
      const next = new Set(prev);
      if (next.has(associateId)) {
        next.delete(associateId);
      } else if (next.size < 2) {
        next.add(associateId);
      }
      return next;
    });
  };

  const handleToggleExpanded = (fclmFunctionId: string) => {
    setExpandedPaths((prev) => ({
      ...prev,
      [fclmFunctionId]: !prev[fclmFunctionId],
    }));
  };

  const handleVerifyClick = () => {
    // Open the "Are you sure you want to submit the plan?" confirmation
    // modal. The actual submission happens in `handleSubmitPlan` below.
    setVerifyModalOpen(true);
  };

  const handleVerifyModalBack = () => {
    setVerifyModalOpen(false);
  };

  const handleSubmitPlan = async () => {
    // Close the confirmation modal first so it doesn't stack with the
    // global success notification rendered by ErrorModal.
    setVerifyModalOpen(false);

    /* istanbul ignore if -- defensive: planId is always set when page loads via Redux */
    if (!planId) {
      dispatch(showError({
        title: 'Publishing Error',
        message: 'Plan ID is required to publish.',
      }));
      return;
    }

    try {
      // Mirror the Review and Publish screen: after the plan is published,
      // update the shift plan status to APPROVED for this plan.
      await publishShiftPlan(planId, getClockWindowNameBasedOnSelectedOption(shift));

      // The success popup mirrors the Shift Plan publish flow: dispatch a
      // success notification (a Modal with an "OK" button rendered by the
      // global ErrorModal). Navigate to the initial Station Assignment
      // landing page so when the user clicks OK they're already there.
      dispatch(
        showNotification({
          title: 'Plan Submitted',
          message: 'Station assignment plan submitted successfully!',
          type: 'success',
        }),
      );

      // Reset the workflow state and return to the initial landing page so
      // the user can start a new plan.
      dispatch(clearStationAssignment());
      navigate(ROUTES.SHIFT_ROTATION);
    } catch (error: any) {
      dispatch(showError({
        title: 'Publishing Error',
        message: error.message || 'Failed to publish station assignment plan. Please try again.',
      }));
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box className="assignments-page" spacingInset="400">
        <div
          className="assignments-page__loading"
          data-testid="assignments-loader"
        >
          <Loader />
        </div>
      </Box>
    );
  }

  if (error || !response) {
    return (
      <Box className="assignments-page" spacingInset="400">
        <Alert type="error" data-testid="assignments-error">
          {error ?? 'Failed to load assignments data.'}
        </Alert>
      </Box>
    );
  }

  const assigningAssociates: AssignmentAssociate[] = assigningAssociateIds
    .map(
      (id) =>
        (activeHalf?.associates ?? []).find((a) => a.associateId === id) ?? null,
    )
    .filter((a): a is AssignmentAssociate => a !== null);

  return (
    <Box className="assignments-page" spacingInset="400">
      <div className="assignments-page__layout">
        {/* Left column: unassigned associate sidebar. */}
        <div className="assignments-page__sidebar">
          <AssociateSidebar
            unassignedAssociates={filteredUnassignedAssociates}
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            onAssignClick={handleAssignClick}
          />
        </div>

        {/* Right column: metrics bar, stacked process path sections. */}
        <div className="assignments-page__main">
          {activeHalf && <MetricsBar metrics={activeHalf.metrics} />}

          {selectedAssociateIds.size === 2 && (
            <Row alignmentHorizontal="end">
              <Button
                type="primary"
                onClick={() => setSwapModalOpen(true)}
                data-testid="swap-button"
              >
                Swap
              </Button>
            </Row>
          )}

          <div className="assignments-page__sections">
            {processPaths.map((path) => (
              <ProcessPathSection
                key={path.fclmFunctionId}
                processPath={path}
                expanded={expandedPaths[path.fclmFunctionId] ?? true}
                onToggleExpanded={handleToggleExpanded}
                onAssociateRemove={handleAssociateRemove}
                onAddAssociate={handleAddAssociate}
                selectedAssociateIds={selectedAssociateIds}
                onToggleSelect={handleToggleSelect}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Footer: Last Modified info + Verify action. */}
      <div className="assignments-page__footer">
        <Text color="secondary" data-testid="assignments-last-modified">
          Last Modified: {response.lastModifiedDate} ({response.lastModifiedBy})
        </Text>
        <div className="assignments-page__footer-actions">
          <Button
            type="primary"
            onClick={handleVerifyClick}
            data-testid="assignments-verify-button"
          >
            Verify
          </Button>
        </div>
      </div>

      {/* Workstation picker modal. Opens when the sidebar's "Assign to…"
          button fires; closes on workstation pick or explicit cancel. The
          same workstation is applied to every checked associate. */}
      <Modal
        open={assigningAssociateIds.length > 0}
        onClose={handleCloseAssignModal}
        title="Assign to workstation"
        scrollContainer="viewport"
      >
        <Box
          className="assignments-page__assign-modal-body"
          data-testid="assignments-assign-modal"
        >
          {assigningAssociates.length > 0 && (
            <Text type="b300">
              {assigningAssociates.length === 1
                ? `Select a workstation for ${assigningAssociates[0].associateName}:`
                : `Select a workstation for ${assigningAssociates.length} associates:`}
            </Text>
          )}
          <div className="assignments-page__assign-modal-list">
            {allWorkstations.map(({ processPath, workstation }) => (
              <Button
                key={workstation.workstationId}
                type="tertiary"
                onClick={() => handleWorkstationPick(workstation.workstationId)}
                data-testid={`assignments-pick-${workstation.workstationId}`}
              >
                {processPath.processPathName} — {workstation.workstationName}
              </Button>
            ))}
          </div>
        </Box>
        <ModalFooter>
          <Button type="secondary" onClick={handleCloseAssignModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Verify-confirmation modal. Asks the user to confirm before
          submitting the assignment plan. "Back" returns to the page in
          its current state; "Submit" dispatches the success notification
          and navigates to the initial Station Assignment landing page.

          Uses the shared AppModal so the look matches other two-button
          confirmations across the app. */}
      <AppModal
        open={verifyModalOpen}
        title="Submit plan"
        message="Are you sure you want to submit the plan?"
        variant="confirmation"
        primaryLabel="Submit"
        secondaryLabel="Back"
        onPrimary={handleSubmitPlan}
        onSecondary={handleVerifyModalBack}
        onClose={handleVerifyModalBack}
        data-testid="assignments-verify-modal"
      />

      {/* Search Associate modal triggered by workstation "Add associate" button */}
      <SearchAssociateModal
        open={assignModalTarget !== null}
        onClose={handleCloseSearchModal}
        associates={activeHalf?.associates ?? []}
        processPaths={processPaths}
        onSubmit={handleAssignSubmit}
        target={assignModalTarget ?? { workstationId: '', workstationName: '', fclmFunctionId: '', fclmProcessPathId: 0 }}
      />

      {/* Swap modal: opens when 2 associates are selected and Swap is clicked */}
      <SearchAssociateModal
        open={swapModalOpen}
        onClose={handleCloseSwapModal}
        associates={activeHalf?.associates ?? []}
        processPaths={processPaths}
        onSubmit={handleSwapSubmit}
        target={{ workstationId: '', workstationName: '', fclmFunctionId: '', fclmProcessPathId: 0 }}
        swapAssociates={
          selectedAssociateIds.size === 2
            ? (() => {
                const [id1, id2] = Array.from(selectedAssociateIds);
                const a1 = findAssociateById(id1);
                const a2 = findAssociateById(id2);
                return a1 && a2 ? [a1, a2] : undefined;
              })()
            : undefined
        }
      />
    </Box>
  );
};

export default AssignmentsPage;

import './index.scss';